import React, { ReactElement } from 'react';
import Link from 'next/link';
import _ from "lodash";
import { Rating } from "@kursk/components/ui/Rating/Rating";
import { IArticle } from "@common/redux/types/articles";
import { IMovie } from "@common/redux/types/movie";
import { MovieHeaderDescription } from "./components/MovieDescription/MovieHeaderDescription";
import { getAddress } from "@kursk/utils/getAddress";
import { BuyButton } from "@kursk/components/ui/BuyButton/BuyButton";
import { EntityType } from "@common/redux/types/common";
import moment from "moment";
import { ITicketsBundles } from "@common/redux/types/ticketsBundles";
import classNames from "classnames";
import { regionContent } from '@kursk/content/regionContent';
import { PublicFavoritesButton } from "@kursk/components/ui/FavoritesButton/PublicFavoritesButton";
import { getConfig } from "@root/config/config";

import styles from './HeaderBlock.module.scss';
import { HeaderBlockImages } from "./components/HeaderBlockImages/HeaderBlockImages";
import { IHeaderBlockProps } from "./types/types";

const getEntityType = (entityType: EntityType) => {
  switch (entityType) {
    case "events":
      return 'События';
    case "places":
      return 'Места';
    case "routes":
      return _.capitalize(regionContent.entitiesNames.routes.plural);
    case "touristroutes":
      return _.capitalize(regionContent.entitiesNames.touristroutes.plural);
    case "overviews":
      return 'Обзоры';
    case "news":
      return 'Новости';
    case "regionvideos":
      return 'ПОСМОТРЕТЬ РЕГИОН';
    case "hotels":
      return 'Отели';
    case "restaurants":
      return 'Рестораны';
    case "ticketsbundles":
      return "абонемент";
    case "movies":
      return "афиша";
    case "artefacts":
      return "дополненная реальность";
    case "museums":
      return "Виртуальные Музеи";
    case "halls":
      return "Виртуальные Залы";
    case "excursions":
      return "Экскурсии";
    case "tours":
      return "Туры";
  }
  return ''
}

const isAstrakhanRegion = getConfig().theme === 'astrakhan';

export const HeaderBlock = ({
  entity,
  goBack,
  goBackTitle,
  entityType,
  firstBreadCrumbLink,
  customBreadCrumb,
  customCategoryName,
  customBuyButton,
}: IHeaderBlockProps): ReactElement => {
  const name = entity?.name || '';
  const ageRestriction = 'ageRestriction' in entity ? entity.ageRestriction : null;
  const hasAgeRestriction = ageRestriction || ageRestriction === 0;
  const description = entity?.description || '';
  const category = 'category' in entity ? entity.category : null;
  const address = 'address' in entity ? entity.address : null;
  const rating = 'rating' in entity ? entity.rating : null;

  const getDescription = () => {
    switch (entityType) {
      case "events":
        return description;
      case "places":
        return getAddress(address);
      case "routes":
        return description;
      case "touristroutes":
        return description;
      case "overviews":
        return description;
      case "news":
        return description;
      case "regionvideos":
        return description;
      case "hotels":
        return getAddress(address);
      case "restaurants":
        return getAddress(address);
      case "ticketsbundles": {
        return `Истекает ${moment((entity as ITicketsBundles).end).format('D MMMM YYYY')}`;
      }
      case "movies":
        return <div className={styles.event_header__description_movie}>
          <MovieHeaderDescription movie={entity as IMovie}/>
        </div>;
      case 'museums':
        return getAddress(address);
      case 'halls':
        return getAddress(address);
      case 'excursions':
        return description;
      case 'tours':
        return description;
    }
  }

  return (
    <div className={styles.event_header__wrapper}>
      <div className={styles.event_header__crumbsWrapper}>
        <div className={styles.event_header__breadcrumbs}>
          <span className={styles.event_header__breadcrumbs_type}>
            <Link href={firstBreadCrumbLink || goBack}>
              {getEntityType(entityType)}
            </Link>
          </span>
          {(category && !customBreadCrumb && entityType !== 'regionvideos') && (
            <>
              <span className={styles.event_header__breadcrumbs_divider}/>
              <span className={styles.event_header__breadcrumbs_category}>
                <Link href={`${goBack}/tag-${category.sysName}`}>
                  {customCategoryName || category?.name}
                </Link>
              </span>
            </>
          )}
          {Boolean(customBreadCrumb) && customBreadCrumb.link && (<>
            <span className={styles.event_header__breadcrumbs_divider}/>
            <span className={styles.event_header__breadcrumbs_category}>
              <Link href={customBreadCrumb?.link}>
                {customBreadCrumb?.title}
              </Link>
            </span>
          </>
          )}
        </div>
        <div className={styles.event_header__return}>
          <Link href={goBack}>
            {goBackTitle}
          </Link>
        </div>
      </div>
      <div className={styles.event_header__main}>
        <div className={styles.event_header__infoWrapper}>
          <div className={styles.event_header__typography}>
            <div className={styles.event_header__type}>
              <span>
                {customCategoryName || (category?.name ? category.name : getEntityType(entityType))}
              </span>

              <div className={styles.event_header__ageAndFavoriteContainer}>
                {hasAgeRestriction && (<span className={styles.event_header__age}>
                  {ageRestriction}+
                </span>)}
                <span>
                  <PublicFavoritesButton
                    key={entity._id}
                    entityId={`${entity._id}`}
                    entityType={entityType}
                    entity={entity}
                  />
                </span>
              </div>
            </div>
            <h1 className={styles.event_header__title}>
              {name}
            </h1>
            <div className={classNames(entityType === 'ticketsbundles' ?
              styles.event_header__expire : styles.event_header__description)}>
              {getDescription()}
            </div>
            {entityType === 'hotels' && rating ? <Rating rating={rating}/> : null}
            {isAstrakhanRegion && entityType === 'news' ? (
              <div className={styles.event_header__date}>
                {moment.tz((entity as IArticle).updateDate, regionContent.timezone).format('DD MMMM YYYY')}
              </div>
            ) : null}
          </div>
          {entityType !== 'regionvideos' && (
            <div className={styles.event_header__actionsWrapper}>
              {customBuyButton ? customBuyButton : (
                <BuyButton
                  entityType={entityType}
                  entityId={entity._id}
                  dataTestId="buy-button-header"
                />
              )}
            </div>
          )}
        </div>
        <HeaderBlockImages entityType={entityType} entity={entity} />
      </div>
    </div>
  )
}
